import React, { useState, useEffect, Fragment, forwardRef } from 'react';
import { Row, Label, Col, Button, Input, Form, FormGroup, Card } from "reactstrap";
import { toast } from "react-toastify";
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from "react-redux";
import Dropzone from 'react-dropzone-uploader';
import RequiredIndicator from '../../../../partial/RequiredIndicator';
import { mykyc, setDealerPanNSDLVerify } from '../../../../../redux/kyc/action';
import { updateDealer, validatePanCard } from '../../../../../api';
import handleupload from '../../../../../utilities/handleUpload';
import validateFile from '../../../../../utilities/validateFile';
import PDFViewer from '../../../../../utilities/PDFViewer';
import { Edit, Info, InfoOutlined } from "@mui/icons-material";
import FileUploadComponent from '../../../../common/FileUpload';
import { KYC_NOTE_1, KYC_NOTE_2, KYC_NOTE_EDIT_1, KYC_NOTE_EDIT_2, verifyButton } from '../../../../../constant';
import KycEditNote from '../../kyc_note/KycEditNote';
import KycViewNote from '../../kyc_note/KycViewNote';
import { getFromLocalStorage, setInLocalStorage } from '../../../../../hooks/useLocalStorage';
import specialCharacterConstrains from '../../../../../utilities/specialCharacterConstrains';
import Swal from 'sweetalert2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';

const signatoryRelationshipOptions = [
    { value: "", label: "Select Entity Type" },
    { value: "Proprietary", label: "Proprietor" },
    { value: "Partner", label: "Partner" },
    { value: "Director", label: "Director" },
    { value: "Authorised Signatory", label: "Authorised Signatory" },
    { value: "Other", label: "Other" }
];

const Pan = (props) => {
    const { activeStep, setActiveStep, setStep1Valid, setStep2Valid, dealerKycData, is_kyc_submitted, is_kyc_active, setIsUpdated, partnershipData, isGstVerified, re_kyc } = props;
    const [gstnStateCode] = useState(27);
    const [loading, setLoading] = useState(false);
    const showReadOnlyPan = (is_kyc_submitted || is_kyc_active);

    const [panUrl, setPanUrl] = useState("");
    const { handleSubmit, register, setValue, getValues, errors, setError, trigger, watch } = useForm();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [ispanVerify, setIsPanVerify] = useState(false);
    const [isPanVerification, setIsPanVerification] = useState(false);
    const [panVerifiedUserName, setPanVerifiedUserName] = useState("");
    const [verifyLoading, setVerifyLoading] = useState(false);

    // For signatory relationship and proprietary logic
    const [showProprietor, setShowProprietor] = useState(false);

    // Redux data
    const kycData = useSelector((content) => content.Kyc);
    const dispatch = useDispatch();

    // Watch signatory_relationship for proprietary logic
    const signatoryRelationship = watch("signatory_relationship");

    useEffect(() => {
        if (signatoryRelationship === "Proprietor") {
            setShowProprietor(true);
        } else {
            setShowProprietor(false);
        }
    }, [signatoryRelationship]);

    const submitForm = async (data) => {
        const storedData = await kycData.panCard;
        if (storedData.panNSDLVerify) {
            setLoading(true);
            setIsSubmitting(true);
            dispatch(mykyc({
                ...kycData,
                panCard: { ...kycData.panCard, ...data },
                authorizedSignatory: {
                    ...kycData.authorizedSignatory,
                    signatory_relationship: data.signatory_relationship || "",
                    is_proprietor: data.signatory_relationship === "Proprietor"
                },
                txnid: Date.now()
            }));
            if (activeStep === 0 && isGstVerified) {
                setIsSubmitting(false);
                setStep1Valid(true);
                setActiveStep((prevActiveStep) => prevActiveStep + 1);
            }
            else {
                setIsSubmitting(false);
                setStep1Valid(true);
                setStep2Valid(true);
                setActiveStep((prevActiveStep) => prevActiveStep + 1);
            }
            setIsUpdated(true)
            setTimeout(() => {
                setIsUpdated(false)
            }, 2000);
        } else {
            toast.error("PAN details not verified");
            return
        }
    };

    const verifyPanDetails = async (data) => {
        setIsPanVerification(false);
        const travelPartnerName = partnershipData?.name;
        const user_pan_card_number = data;
        if (!user_pan_card_number) {
            toast.error("Please enter PAN card number");
            return
        }
        setVerifyLoading(true);
        setIsPanVerify(true);
        try {
            const panNSDLPayload = { panNumber: user_pan_card_number }
            const panNSDLVerifiedRes = await validatePanCard(panNSDLPayload);

            if (panNSDLVerifiedRes?.status == 200) {
                const pan_verified_user_name = panNSDLVerifiedRes?.data?.result?.msg?.name;

                if (travelPartnerName?.trim()?.toLowerCase() == pan_verified_user_name?.trim()?.toLowerCase()) {
                    setPanVerifiedUserName(travelPartnerName);
                    dispatch(setDealerPanNSDLVerify(true));
                    toast.success("Details are verified");
                    setVerifyLoading(false);
                } else {
                    Swal.fire({
                        icon: 'info',
                        html: `Please verify that the name <b>${pan_verified_user_name}</b> captured matches the PAN document. Kindly note that further name changes will not be permitted after KYC submission`,
                        showCancelButton: true,
                        confirmButtonText: 'Confirm',
                        cancelButtonText: 'Cancel',
                    }).then(async (result) => {
                        if (result.isConfirmed) {
                            setPanVerifiedUserName(pan_verified_user_name);
                            dispatch(setDealerPanNSDLVerify(true));
                            toast.success("Travel Partner name updated successfully as per PAN details");
                            setVerifyLoading(false);
                        } else {
                            setIsPanVerify(false);
                            setVerifyLoading(false);
                        }
                    });
                    setIsPanVerification(true);
                }
            } else {
                return false
            }
        } catch (error) {
            toast.error(error?.response?.data?.error ? error?.response?.data?.error : 'Something went wrong');
            setIsPanVerify(false);
            setVerifyLoading(false);
            return;
        }
        setIsUpdated(true)
        setTimeout(() => {
            setIsUpdated(false)
        }, 2000);
    };

    const onSubmitForKycSubmitComplete = () => {
        if (activeStep === 0) {
            setStep1Valid(true);
            setActiveStep((prevActiveStep) => prevActiveStep + 1);
        }
    }

    useEffect(() => {
        const storedData = kycData?.panCard || kycData?.users_kyc_detail?.panCard || {};
        Object.keys(storedData).forEach((fieldName) => {
            setValue(fieldName, storedData[fieldName]);
        });
        setPanUrl(getValues()["user_pan_card_image"]);
        // Set signatory_relationship from redux if present
        if (kycData?.authorizedSignatory?.signatory_relationship) {
            setValue("signatory_relationship", kycData.authorizedSignatory.signatory_relationship);
        }
    }, [kycData.panCard.user_pan_card_name]);

    const panPattern = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;

    return (
        <Fragment>
            <Row>
                <Col xl="12" md="12" sm="12" className="mt-4" key={1}>
                    <div className="project-box">
                        {is_kyc_active ? (<span className={`badge badge-success`}>{"Verified"}</span>) : ''}

                        <div className='mt-3'>
                            {showReadOnlyPan ? (
                                <>
                                    <Row>
                                        <Col xl="6" md="6">
                                            <Row>
                                                <Col xl="12" md="12">
                                                    <FormGroup>
                                                        <Label className='col-form-label fw-10'>{"PAN Card Number"}</Label>
                                                        <Input
                                                            className="form-control"
                                                            name="user_pan_card_number"
                                                            type="text"
                                                            defaultValue={kycData?.panCard?.user_pan_card_number}
                                                            readOnly
                                                        />
                                                    </FormGroup>
                                                </Col>
                                            </Row>
                                            <Row>
                                                <Col xl="12" md="12">
                                                    <FormGroup>
                                                        <Label>{"Name on the Card"}</Label>
                                                        <Input
                                                            className="form-control"
                                                            name="user_pan_card_name"
                                                            type="text"
                                                            defaultValue={kycData?.panCard?.user_pan_card_name}
                                                            readOnly
                                                        />
                                                    </FormGroup>
                                                </Col>
                                            </Row>
                                            <Row>
                                                <Col xl="12" md="12">
                                                    <FormGroup>
                                                        <Label>{"Entity Type"}</Label>
                                                        <Input
                                                            className="form-control"
                                                            name="signatory_relationship"
                                                            type="text"
                                                            defaultValue={kycData?.authorizedSignatory?.signatory_relationship}
                                                            readOnly
                                                        />
                                                    </FormGroup>
                                                </Col>
                                            </Row>
                                            <KycViewNote />
                                        </Col>

                                        <Col xl="6" md="6" className="d-flex justify-content-center">
                                            {/* Second Column Content */}
                                            <FormGroup className='text-center'>
                                                <Label>{"Pan document"}</Label>
                                                <FileUploadComponent name="user_pan_card_image" fileUploadFlag={false} data={kycData?.panCard?.user_pan_card_image} viewOnly={true} />
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                    <Row className="justify-content-end">
                                        <Button color="info" type="submit" onClick={onSubmitForKycSubmitComplete}>
                                            {loading ? "Loading..." : "Next"}
                                        </Button>
                                    </Row>
                                </>
                            ) : (getFromLocalStorage("ROLE")?.key == "DEALERSHIP_MANAGER" || getFromLocalStorage("ROLE")?.key == "CONSOLIDATOR") && (
                                <Form className="form-bookmark needs-validation" onSubmit={handleSubmit(submitForm)} >
                                    <Row>
                                        <Col xl="6" md="6">
                                            <Row>
                                                <Col xl="9" md="9">
                                                    <FormGroup>
                                                        <Label className='col-form-label fw-10'>{"PAN Card Number"}</Label> <RequiredIndicator />
                                                        <Input
                                                            className="form-control"
                                                            name="user_pan_card_number"
                                                            type="text"
                                                            defaultValue={getValues()["user_pan_card_number"]}
                                                            innerRef={register({
                                                                required: 'Pan Number is required',
                                                                pattern: {
                                                                    value: panPattern,
                                                                    message: "Invalid Pan Number"
                                                                }
                                                            })}
                                                            onChange={(e) => {
                                                                setIsPanVerification(false);
                                                                const upperCasePan = e.target.value.toUpperCase();
                                                                e.target.value = upperCasePan;
                                                                dispatch(mykyc({ ...kycData, gstn: { ...kycData.gstn, gstn_number: gstnStateCode + upperCasePan }, txnid: Date.now() }))
                                                            }}
                                                            readOnly={kycData?.panCard?.panNSDLVerify ? true : false}
                                                        />
                                                        <span style={{ color: "red" }}>
                                                            {errors.user_pan_card_number && errors?.user_pan_card_number?.message}
                                                        </span>
                                                    </FormGroup>
                                                </Col>
                                                <Col xl="3" md="3" className="verify-button">
                                                    {kycData?.panCard?.panNSDLVerify ?
                                                        <Button color="success" className="verify-pan-button" disabled>
                                                            <FontAwesomeIcon icon={faCheck} />
                                                        </Button>
                                                        :
                                                        <Button color="info"
                                                            className="verify-pan-button"
                                                            onClick={() => verifyPanDetails(getValues()["user_pan_card_number"])}
                                                            disabled={ispanVerify}> {verifyLoading ? 'Loading...' : verifyButton} </Button>
                                                    }
                                                </Col>
                                            </Row>
                                            <Row>
                                                <Col xl="12" md="12">
                                                    <FormGroup>
                                                        <Label>{"Name on the Card"}</Label> <RequiredIndicator />
                                                        <Input
                                                            className="form-control"
                                                            name="user_pan_card_name"
                                                            type="text"
                                                            innerRef={register(specialCharacterConstrains(true, "Name on the Card is required"))}
                                                            value={panVerifiedUserName ?? getValues()["user_pan_card_name"] ?? ""}
                                                            readOnly
                                                        />
                                                        <span style={{ color: "red" }}>
                                                            {errors?.user_pan_card_name?.message}
                                                        </span>
                                                    </FormGroup>
                                                </Col>
                                            </Row>
                                            <Row>
                                                <Col xl="12" md="12">
                                                    <FormGroup>
                                                        <Label>{"Entity Type"}</Label> <RequiredIndicator />
                                                        <Input
                                                            className="form-control"
                                                            name="signatory_relationship"
                                                            type="select"
                                                            innerRef={register({ required: "Signatory Relationship is required" })}
                                                            defaultValue={getValues()["signatory_relationship"] || ""}
                                                        >
                                                            {signatoryRelationshipOptions.map(opt => (
                                                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                            ))}
                                                        </Input>
                                                        <span style={{ color: "red" }}>
                                                            {errors.signatory_relationship && errors.signatory_relationship.message}
                                                        </span>
                                                    </FormGroup>
                                                </Col>
                                            </Row>
                                            {showProprietor && (
                                                <Row>
                                                    <Col xl="12" md="12">
                                                        <FormGroup>
                                                            <Label className='text-info'>You have selected Proprietor as relationship. Please ensure all details match the proprietor's PAN.</Label>
                                                        </FormGroup>
                                                    </Col>
                                                </Row>
                                            )}
                                            <KycEditNote />
                                        </Col>

                                        <Col xl="6" md="6" className="d-flex justify-content-center">
                                            <FormGroup className='text-center'>
                                                <Label>{"Upload PAN document"}</Label> <RequiredIndicator />
                                                <FileUploadComponent
                                                    register={register}
                                                    name="user_pan_card_image"
                                                    setValue={setValue}
                                                    trigger={trigger}
                                                    getValues={getValues}
                                                    data={panUrl}
                                                    add={getValues()["user_pan_card_image"]?.length > 0 ? false : true}
                                                    pdfImageOnly={true}
                                                />
                                                <span style={{ color: "red" }}>
                                                    {errors.user_pan_card_image && "PAN document is required"}
                                                </span>
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                    <Row className="justify-content-end">
                                        <Button color="info" type="submit" disabled={isSubmitting}>
                                            {isSubmitting ? 'Loading...' : 'Save'} <span>&gt;&gt;</span>
                                        </Button>
                                    </Row>
                                </Form>
                            )}
                        </div>
                    </div>
                </Col>
            </Row>
        </Fragment>
    )
};

export default Pan;